import React, { useState, useContext } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui';
import { auth } from '../services/firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { AuthContext } from '../contexts/AuthContext';
import { LinksIDService } from '../services/api';

/**
 * Authentication Modal for Login/Signup
 *
 * Two options:
 *   1. "Sign in with LinksID" — redirects to LinksID auth page (Google/Apple/etc live there)
 *   2. Email / Password — direct Firebase auth, still creates a LinksID passport
 */
export function AuthModal({ isOpen, onClose, onAuthSuccess }) {
  const [mode, setMode] = useState('login'); // 'login' or 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passportStatus, setPassportStatus] = useState(''); // '', 'resolving', 'resolved', 'failed'
  const { refreshPassport } = useContext(AuthContext);

  if (!isOpen) return null;

  const LINKSID_AUTH_URL = import.meta.env.VITE_LINKSID_AUTH_URL
    || 'https://linksid.colewinds-dev.com/auth/login';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPassportStatus('');
    setLoading(true);

    try {
      let userCredential;

      if (mode === 'signup') {
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
        if (displayName) {
          await updateProfile(userCredential.user, { displayName });
        }
        // Migrate guest data if exists
        const guestId = localStorage.getItem('guestId');
        if (guestId) {
          try {
            const token = await userCredential.user.getIdToken();
            await fetch('/api/profile/migrate-guest-data', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ guest_id: guestId })
            });
            localStorage.removeItem('guestId');
          } catch (migrationError) {
            console.error('Guest data migration failed:', migrationError);
          }
        }
      } else {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
      }

      const token = await userCredential.user.getIdToken();

      // Wait for LinksID passport resolution before closing modal
      setPassportStatus('resolving');
      try {
        await LinksIDService.getPassport(token);
        setPassportStatus('resolved');
      } catch (passportErr) {
        console.error('[LinksID] Passport creation failed:', passportErr);
        setPassportStatus('failed');
        setError('Signed in, but LinksID passport creation failed. Your data may not sync across apps.');
        // Don't block close — auth itself succeeded
      }

      if (onAuthSuccess) {
        onAuthSuccess(userCredential.user);
      }
      onClose();
    } catch (err) {
      console.error('Auth error:', err);
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
      setPassportStatus('');
    }
  };

  const handleLinksIDSignIn = () => {
    // Redirect to LinksID auth page — Google/Apple/social logins live there
    const returnUrl = encodeURIComponent(window.location.origin + '/auth/callback');
    window.location.href = `${LINKSID_AUTH_URL}?return_url=${returnUrl}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full mx-4 border border-zinc-800">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">
            {mode === 'login' ? 'Sign In' : 'Create Account'}
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Primary: Sign in with LinksID */}
        <button
          onClick={handleLinksIDSignIn}
          disabled={loading}
          className="w-full bg-emerald-700 hover:bg-emerald-600 text-white font-semibold py-2.5 px-4 rounded flex items-center justify-center gap-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
          {loading ? (passportStatus === 'resolving' ? 'Setting up passport...' : 'Please wait...') : 'Sign in with LinksID'}
        </button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-zinc-800"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-zinc-900 text-zinc-500">Or continue with email</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">
                Display Name (optional)
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:outline-none focus:border-primary"
                placeholder="John Doe"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:outline-none focus:border-primary"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:outline-none focus:border-primary"
              placeholder="••••••••"
            />
            {mode === 'signup' && (
              <p className="text-xs text-zinc-500 mt-1">Minimum 6 characters</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full"
          >
            {loading ? (passportStatus === 'resolving' ? 'Setting up passport...' : 'Please wait...') : mode === 'login' ? 'Sign In' : 'Create Account'}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={() => {
              setMode(mode === 'login' ? 'signup' : 'login');
              setError('');
            }}
            className="text-primary hover:underline text-sm"
          >
            {mode === 'login'
              ? "Don't have an account? Sign up"
              : 'Already have an account? Sign in'}
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-zinc-800">
          <p className="text-xs text-zinc-500 text-center">
            Your identity is powered by <span className="text-emerald-400 font-medium">LinksID</span> — one login across all your golf apps
          </p>
        </div>
      </div>
    </div>
  );
}

export default AuthModal;
